/* ============================================
   QUOTE WORKSTATION — Items & Vendor Preferences
   Asset: workstation-items.js
   Renders the items table, handles add/duplicate,
   and manages vendor option preference memory.
   ============================================ */
(function() {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;

  var state = Q.state;
  var dom = Q.dom;
  var wrap = Q.wrap;
  var collapsedVendors = Q.collapsedVendors;
  var escHtml = Q.escHtml;
  var escAttr = Q.escAttr;
  var fmt = Q.fmt;
  var genId = Q.genId;
  var normalizeVendor = Q.normalizeVendor;
  var autoPoints = Q.autoPoints;
  var calculateDirect = Q.calculateDirect;
  var getUnitToggleConfig = Q.getUnitToggleConfig;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-16) — Vendor reorder chevrons. Same inline-SVG
     approach as the drag handle: no icon font, inherits currentColor so the hover state is
     one CSS rule. */
  var VENDOR_MOVE_UP_SVG = '<svg viewBox="0 0 10 6" width="9" height="6" aria-hidden="true"><path d="M1 5L5 1l4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var VENDOR_MOVE_DOWN_SVG = '<svg viewBox="0 0 10 6" width="9" height="6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* ── Render Items Table ── */
  function renderItems() {
    if (Q.catalogReady) (state.items || []).forEach(labelOptionSubs);   // CUSTOM_QWOPTIONLABEL_BY_CLAUDE
    // Guard: DOM may not exist yet (import runs before auth gate reveals content)
    if (!dom.itemsContainer) {
      dom.itemsContainer = wrap.querySelector('.rq-ws-items');
      dom.itemsEmpty = wrap.querySelector('.rq-ws-items__empty');
      if (!dom.itemsContainer) return;
    }
    // If container is hidden (auth gate not revealed yet), retry after reveal
    if (state.items.length > 0 && dom.itemsContainer.offsetHeight === 0) {
      document.addEventListener('rep-portal:authenticated', function retry() {
        document.removeEventListener('rep-portal:authenticated', retry);
        renderItems();
        Q.recalcTotals();
      });
      return;
    }
    if (state.items.length === 0) {
      dom.itemsEmpty.style.display = '';
      // Remove vendor groups
      var groups = dom.itemsContainer.querySelectorAll('.rq-ws-vendor-group');
      for (var i = 0; i < groups.length; i++) groups[i].remove();
      Q.recalcTotals();
      return;
    }
    dom.itemsEmpty.style.display = 'none';

    // Sort a copy: series items sorted by weight within their group
    var sortedItems = state.items.slice();
    sortedItems.sort(function(a, b) {
      // Keep original order for non-series items
      if (!a.seriesId && !b.seriesId) return 0;
      // Series items from the same series: sort by weight
      if (a.seriesId && b.seriesId && a.seriesId === b.seriesId) {
        return (a.seriesWeight || 0) - (b.seriesWeight || 0);
      }
      return 0;
    });

    // Group by vendor
    var vendorMap = {};
    var vendorOrder = [];
    sortedItems.forEach(function(item) {
      var v = item.vendor || 'Other';
      if (!vendorMap[v]) { vendorMap[v] = []; vendorOrder.push(v); }
      vendorMap[v].push(item);
    });

    // Build HTML
    var html = '';
    var globalIdx = 0;
    vendorOrder.forEach(function(vendor, vendorIdx) {
      var items = vendorMap[vendor];
      html += '<div class="rq-ws-vendor-group">';
      var isCollapsed = collapsedVendors[vendor] ? true : false;
      html += '<div class="rq-ws-vendor-group__header" data-vendor-toggle="' + escAttr(vendor) + '">';
      html += '<button class="rq-ws-vendor-group__collapse" type="button">' + (isCollapsed ? '+' : '&minus;') + '</button>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-01) — the heading READS as the byline, but
         `data-vendor` stays the canonical name: it is the key every handler on this row uses to
         find the group's items, so labelling it would break renaming, +5 and collapse. */
      var vendorLabel = (state.vendorLabels && state.vendorLabels[vendor]) || vendor;
      html += '<button class="rq-ws-vendor-group__name" data-vendor="' + escAttr(vendor) + '" type="button" title="Click to change vendor">' + escHtml(vendorLabel) + ' &#9998;</button>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — +5 quick-add button. Appends 5 blank
         custom rows directly to this vendor's group without opening the
         vendor prompt — the vendor is already known. Sits at the right of
         the header; the reorder-arrow span below now carries the
         margin-left:auto that pushes this whole cluster over (2026-09-16).
         Inline style keeps the change self-contained (no CSS file edit).
         Hover style matches the collapse button family. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-16) — Vendor reorder arrows. Moves this whole
         vendor block past its neighbour; the handler in workstation-table.js re-anchors the page
         scroll afterwards so the group the rep clicked does not move on screen.

         The span carries the margin-left:auto that used to sit on the +5 button, so the right-hand
         cluster keeps its alignment even on a one-vendor quote, where the arrows are omitted
         entirely (nothing to swap with, and two dead buttons read as broken).

         `data-move-vendor` rather than `data-vendor`: the name/pencil button already owns
         `data-vendor` on this same header, and the collapse branch keys off that. */
      html += '<span class="rq-ws-vendor-group__move" style="margin-left:auto;">';
      if (vendorOrder.length > 1) {
        html += '<button class="rq-ws-vendor-group__move-btn" data-vendor-move="up" data-move-vendor="' + escAttr(vendor) + '" type="button" title="Move ' + escAttr(vendorLabel) + ' up" aria-label="Move ' + escAttr(vendorLabel) + ' up"' + (vendorIdx === 0 ? ' disabled' : '') + '>' + VENDOR_MOVE_UP_SVG + '</button>';
        html += '<button class="rq-ws-vendor-group__move-btn" data-vendor-move="down" data-move-vendor="' + escAttr(vendor) + '" type="button" title="Move ' + escAttr(vendorLabel) + ' down" aria-label="Move ' + escAttr(vendorLabel) + ' down"' + (vendorIdx === vendorOrder.length - 1 ? ' disabled' : '') + '>' + VENDOR_MOVE_DOWN_SVG + '</button>';
      }
      html += '</span>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += '<button class="rq-ws-vendor-group__addn" data-vendor-addn="' + escAttr(vendor) + '" type="button" title="Add 5 blank rows to ' + escAttr(vendor) + '" style="padding:2px 10px;border:1px solid var(--rq-border,#d1d5db);border-radius:6px;background:var(--rq-bg-surface,#fff);color:var(--rq-text,#000F1C);font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;line-height:1.4;">+5</button>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += '</div>';
      html += '<div class="rq-ws-vendor-group__body' + (isCollapsed ? ' rq-ws-vendor-group__body--collapsed' : '') + '">';
      html += '<table class="rq-ws-table">';
      html += '<colgroup><col style="width:auto"><col style="width:100px"><col style="width:60px"><col style="width:90px"><col style="width:90px"><col style="width:100px"><col style="width:100px"><col style="width:4px"><col style="width:100px"><col style="width:110px"></colgroup>';
      html += '<thead><tr>';
      html += '<th>Item</th><th>SKU</th><th>Qty</th><th>MSRP</th><th>MAP</th><th>Direct</th><th>Total</th><th class="rq-ws-table__divider-th"></th><th>Cost</th><th>&nbsp;&nbsp;&nbsp;Pts</th>';
      html += '</tr></thead><tbody>';

      // Find last item in each series (for sub-description placement)
      var lastSeriesItemId = {};
      items.forEach(function(item) {
        if (item.seriesId) lastSeriesItemId[item.seriesId] = item.id;
      });

      // Deduplicate vendor-shared sub-descriptions: only show on bottom-most item per vendor

      // For each vendor, find the last item and track which prefixes are vendor-color subs
      var lastItemByVendor = {};
      items.forEach(function(item) { if (item.vendor) lastItemByVendor[item.vendor] = item.id; });

      // Build per-item set of sub-descs to actually render
      // Shared vendor color subs (same value across all items) → show once on last item
      // Item-specific vendor color subs (different values or unique to one item) → show under each item
      // Duplicate items (same productHandle twice) are always independent — excluded from shared detection
      var sharedSubPrefixes = {};
      // Identify duplicate productHandles in this vendor group
      var handleCount = {};
      items.forEach(function(item) { if (item.productHandle) handleCount[item.productHandle] = (handleCount[item.productHandle] || 0) + 1; });
      var dupHandles = {};
      Object.keys(handleCount).forEach(function(h) { if (handleCount[h] > 1) dupHandles[h] = true; });
      if (items.length > 1) {
        // Collect all vendor color sub values per prefix, tracking which items have each prefix
        // Exclude items with duplicate handles — their subs are always item-specific
        var prefixValues = {};
        var prefixItemCount = {};
        items.forEach(function(item) {
          if (!item.subDescriptions) return;
          if (item.productHandle && dupHandles[item.productHandle]) return;
          var seenInItem = {};
          item.subDescriptions.forEach(function(sd) {
            if (!isVendorColorSubDesc(sd)) return;
            var prefix = subDescPrefix(sd);
            if (!prefixValues[prefix]) prefixValues[prefix] = {};
            prefixValues[prefix][sd] = true;
            if (!seenInItem[prefix]) { seenInItem[prefix] = true; prefixItemCount[prefix] = (prefixItemCount[prefix] || 0) + 1; }
          });
        });
        // "Shared" = multiple non-duplicate items have it AND they all have the same value
        Object.keys(prefixValues).forEach(function(prefix) {
          if (prefixItemCount[prefix] > 1 && Object.keys(prefixValues[prefix]).length === 1) {
            sharedSubPrefixes[prefix] = true;
          }
        });
      }

      var itemVisibleSubs = {};
      items.forEach(function(item) {
        var visible = [];
        var isDup = item.productHandle && dupHandles[item.productHandle];
        if (item.subDescriptions && item.subDescriptions.length > 0) {
          var seenPrefixes = {};
          item.subDescriptions.forEach(function(sd) {
            var prefix = subDescPrefix(sd);
            if (isVendorColorSubDesc(sd)) {
              if (sharedSubPrefixes[prefix] && !isDup) {
                // Shared across group — only render on last item, dedup by prefix
                if (item.id === lastItemByVendor[item.vendor] && !seenPrefixes[prefix]) {
                  seenPrefixes[prefix] = true;
                  visible.push(sd);
                }
              } else {
                // Item-specific or duplicate item — render under this item
                if (!seenPrefixes[prefix]) {
                  seenPrefixes[prefix] = true;
                  visible.push(sd);
                }
              }
            } else {
              visible.push(sd);
            }
          });
        }
        itemVisibleSubs[item.id] = visible;
      });

      var renderedSeriesHeaders = {};
      items.forEach(function(item) {
        globalIdx++;

        // Track first item in series (for top border)
        if (item.seriesId && !renderedSeriesHeaders[item.seriesId]) {
          renderedSeriesHeaders[item.seriesId] = true;
        }

        var lineTotal = item.isFree ? 0 : (item.directPrice || 0) * (item.quantity || 0);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Indented child item. Set when admin
           drags the row handle slightly right; flagged on item.indented.
           Renders an indent on the Item cell so the workstation preview
           matches what the printed PDF will show. */
        var indentClass = item.indented ? ' rq-ws-row--indented' : '';
        html += '<tr data-item-id="' + item.id + '" data-row-vendor="' + escAttr(vendor) + '"' + (item.seriesId ? ' class="rq-ws-series-item' + indentClass + '" data-series-id="' + escAttr(item.seriesId) + '"' : (indentClass ? ' class="' + indentClass.trim() + '"' : '')) + '>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

        // Item cell with sub-descriptions
        /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — resolved ONCE per row: the sub-text rows
           below use it to decide what is a real choice, and the SKU cell uses it to decide whether
           this line gets an Options pill at all. */
        var _optMeta = optionMetaFor(item);
        html += '<td>';
        var isSeriesLast = item.seriesId && lastSeriesItemId[item.seriesId] === item.id;
        var showSubDesc = !item.seriesId || isSeriesLast;
        var visibleSubs = itemVisibleSubs[item.id] || [];
        var hasSubs = visibleSubs.length > 0;

        // Title row: drag handle + toggle + name. Indent applied to the
        // title row only (drag handle stays at the row edge so it's still
        // grabbable from anywhere on the line).
        html += '<div class="rq-ws-item-title-row"' + (item.indented ? ' style="padding-left:18px;"' : '') + '>';
        html += '<button class="rq-ws-table__drag-handle" data-item-id="' + item.id + '" type="button" aria-label="Drag to reorder" title="Drag to reorder"><svg viewBox="0 0 12 16" width="10" height="14" fill="currentColor" aria-hidden="true"><circle cx="3" cy="3" r="1.5"/><circle cx="3" cy="8" r="1.5"/><circle cx="3" cy="13" r="1.5"/><circle cx="9" cy="3" r="1.5"/><circle cx="9" cy="8" r="1.5"/><circle cx="9" cy="13" r="1.5"/></svg></button>';
        if (showSubDesc) {
          html += '<button class="rq-ws-subdesc-toggle' + (hasSubs ? ' rq-ws-subdesc-toggle--open' : '') + '" data-item-id="' + item.id + '" type="button">&ctdot;</button>';
        }
        if (item.seriesId) {
          html += '<div class="rq-ws-series-input-wrap">';
          html += '<input class="rq-ws-table__editable rq-ws-table__item-title rq-ws-series-input" value="' + escAttr(item.seriesBaseTitle || '') + '" data-item-id="' + item.id + '" data-field="seriesBaseTitle" spellcheck="false" autocomplete="off">';
          html += '<span class="rq-ws-series-input-suffix">– ' + escHtml(String(item.seriesWeight)) + ' lbs</span>';
          html += '</div>';
        } else {
          html += '<input class="rq-ws-table__editable rq-ws-table__item-title" value="' + escAttr(item.title) + '" data-item-id="' + item.id + '" data-field="title" spellcheck="false" autocomplete="off">';
        }
        if (item.isFree) html += '<span class="rq-ws-table__item-free">FREE</span>';
        // No-freight toggle — flags in-house / marketing line items so the
        // freight email AND PO generation both skip them. Default off (item
        // ships normally); when on, shows an amber truck-with-slash to make
        // the exclusion visible. The line still appears on the customer's
        // quote/invoice — only supplier-facing communication is filtered.
        var noFreightActive = !!item.excludeFromFreight;
        var slashLine = noFreightActive ? '<line x1="2" y1="14" x2="18" y2="2"/>' : '';
        var noFreightTitle = noFreightActive
          ? 'In-house / marketing item — excluded from supplier freight requests and POs. Click to include.'
          : 'Click to mark as in-house (excluded from supplier freight requests and POs).';
        html += '<button class="rq-ws-table__no-freight' + (noFreightActive ? ' rq-ws-table__no-freight--active' : '') + '" data-item-id="' + item.id + '" type="button" title="' + noFreightTitle + '" aria-label="Toggle in-house / no-freight">'
          + '<svg viewBox="0 0 20 16" width="14" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
          + '<rect x="1" y="3" width="11" height="8" rx="0.5"/><path d="M12 6h4l2.5 2.5V11h-6.5"/><circle cx="4" cy="13" r="2"/><circle cx="14" cy="13" r="2"/>' + slashLine
          + '</svg></button>';
        html += '</div>';

        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Per-item product-link editor (PDF links
           feature). Shown when the "Add website links to products" toggle is on
           and the row has a SKU. The Blue Sky storefront page wins automatically
           server-side; this lets the rep supply an external link for items we
           don't carry on our site yet (saved by SKU, reused on future quotes). */
        // On our site = this item's SKU resolves to a product published to the
        // Online Store (SKU is stable across product renames; resolved server-side).
        // Show the control only when the SKU is CONFIRMED off-site (=== false) so
        // on-site + not-yet-resolved items never flash a button.
        // For a weight sequence, render ONE link control for the whole series
        // (on the last/heaviest row, where the sub-description also sits) instead
        // of one per weight — a saved link still propagates to every member SKU
        // so the PDF links each weight row. Non-series items render normally.
        if (state.linkProductsOnPdf && item.type === 'custom') {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE — Custom (manual-entry) items are never on
             the BSF storefront and are unique to this quote, so their link lives
             ON the item (item.externalLink, saved with the quote) rather than the
             shared per-SKU map. The control shows by DEFAULT whenever the toggle
             is on — no SKU or server resolution required. Keyed by item id. */
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — resolve the link the SAME way
             the PDF does: per-item externalLink FIRST, else the per-SKU store
             (state.externalLinks, populated by fetchExternalLinks). A custom item that
             carries a real SKU (e.g. "Reducer Strip" / ELA1-000019) has its link saved in
             the per-SKU store, so reading only item.externalLink showed "Add link" on
             reload even though the PDF rendered the link. Checking both keeps the QW in
             lockstep with the PDF. */
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — a CUSTOM line item can still carry
             a real SKU that's ON our storefront: hand-built Spirit products added as custom
             lines, or a product that went LIVE on BSF after the quote was built (revamp).
             The PDF links those to Blue Sky, so the QW must too — this branch used to ASSUME
             a custom item is never on our site (the comment above), which stranded them on
             "Add link" forever. When the SKU resolves on-site (onBsf===true from
             fetchExternalLinks), render the Blue Sky pill exactly like the SKU branch; the
             on-site page always wins on the PDF, so any stale external link is moot. */
          var _custOnBsf = item.sku ? (state.onBsf || {})[item.sku] : undefined;
          var _custBsfUrl = item.sku ? ((state.bsfUrls || {})[item.sku] || '') : '';
          if (_custOnBsf === true) {
            html += '<div class="rq-ws-extlink" data-item-id="' + item.id + '" data-sku="' + escAttr(item.sku) + '">';
            if (_custBsfUrl) {
              html += '<a class="rq-ws-extlink__chip rq-ws-extlink__chip--bsf" href="' + escAttr(_custBsfUrl) + '" target="_blank" rel="noopener" title="On Blue Sky — opens the product page">&#128279; Blue Sky link</a>';
            } else {
              html += '<span class="rq-ws-extlink__chip rq-ws-extlink__chip--bsf" title="On Blue Sky">&#128279; Blue Sky link</span>';
            }
            html += '</div>';
          } else if (item.sku && _custOnBsf === undefined && !(Q.linkResolvedSkus || {})[item.sku]) {
            /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — ⛔ "WE HAVE NOT ASKED" IS NOT "NO".

               This branch's `else` used to catch `false` and `undefined` alike, so a line whose SKU
               had never been resolved was offered "Add link" as though we had checked the storefront
               and it was not there. Ryan hit it on TRD-000093-A — a product that IS live and
               published (2026-08-14), sitting on a quote that was resolved before it went live and
               never re-asked, because fetchExternalLinks only ran on quote load and on the toggle.

               The product branch below already draws this distinction and renders nothing while
               resolution is pending, precisely so the control cannot flash the wrong state. Same
               rule here. `item.sku &&` matters: a genuine custom line with NO SKU is never on our
               site and "Add link" is the right and only affordance for it. */
          } else {
            var _custUrl = item.externalLink || ((item.sku && state.externalLinks) ? (state.externalLinks[item.sku] || '') : '') || '';
            html += '<div class="rq-ws-extlink" data-item-id="' + item.id + '" data-custom="1">';
            if (_custUrl) {
              html += '<button class="rq-ws-extlink__chip rq-ws-extlink__chip--manual" data-item-id="' + item.id + '" type="button" title="Click to edit this link">&#128279; External link</button>';
            } else {
              html += '<button class="rq-ws-extlink__add" data-item-id="' + item.id + '" type="button">&#128279; Add link</button>';
            }
            html += '<div class="rq-ws-extlink__editor" data-item-id="' + item.id + '" style="display:none;">';
            html += '<input type="url" class="rq-ws-extlink__input" data-item-id="' + item.id + '" placeholder="https://manufacturer.com/product" value="' + escAttr(_custUrl) + '" spellcheck="false">';
            html += '<button class="rq-ws-extlink__save" data-item-id="' + item.id + '" type="button">Save</button>';
            html += '<button class="rq-ws-extlink__cancel" data-item-id="' + item.id + '" type="button">Cancel</button>';
            if (_custUrl) html += '<button class="rq-ws-extlink__delete" data-item-id="' + item.id + '" type="button">Delete</button>';
            html += '</div>';
            html += '</div>';
          }
        } else if (state.linkProductsOnPdf && item.sku && (!item.seriesId || isSeriesLast)) {
          var _onBsf = (state.onBsf || {})[item.sku];
          var _bsfUrl = (state.bsfUrls || {})[item.sku] || '';
          var _extUrl = (state.externalLinks || {})[item.sku] || item.externalLink || ''; // per-SKU store first, else per-item link — mirrors the PDF resolution
          var _seriesAttr = item.seriesId ? ' data-series-id="' + escAttr(item.seriesId) + '"' : '';
          if (_onBsf === true) {
            // On our site — non-editable Blue Sky link pill (FILLED blue). The
            // BSF storefront page always wins on the PDF, so there's nothing to
            // edit/remove; the pill just confirms it + links to the page.
            html += '<div class="rq-ws-extlink" data-item-id="' + item.id + '" data-sku="' + escAttr(item.sku) + '"' + _seriesAttr + '>';
            if (_bsfUrl) {
              html += '<a class="rq-ws-extlink__chip rq-ws-extlink__chip--bsf" href="' + escAttr(_bsfUrl) + '" target="_blank" rel="noopener" title="On Blue Sky — opens the product page">&#128279; Blue Sky link</a>';
            } else {
              html += '<span class="rq-ws-extlink__chip rq-ws-extlink__chip--bsf" title="On Blue Sky">&#128279; Blue Sky link</span>';
            }
            html += '</div>';
          } else if (_onBsf === false) {
            // Not on our site — rep-added external link (OUTLINED blue). Click the
            // pill itself to edit; the editor carries Save / Cancel / Delete.
            html += '<div class="rq-ws-extlink" data-item-id="' + item.id + '" data-sku="' + escAttr(item.sku) + '"' + _seriesAttr + '>';
            if (_extUrl) {
              html += '<button class="rq-ws-extlink__chip rq-ws-extlink__chip--manual" data-item-id="' + item.id + '" type="button" title="Click to edit this link">&#128279; External link</button>';
            } else {
              html += '<button class="rq-ws-extlink__add" data-item-id="' + item.id + '" type="button">&#128279; Add link</button>';
            }
            html += '<div class="rq-ws-extlink__editor" data-item-id="' + item.id + '" style="display:none;">';
            html += '<input type="url" class="rq-ws-extlink__input" data-item-id="' + item.id + '" placeholder="https://manufacturer.com/product" value="' + escAttr(_extUrl) + '" spellcheck="false">';
            html += '<button class="rq-ws-extlink__save" data-item-id="' + item.id + '" type="button">Save</button>';
            html += '<button class="rq-ws-extlink__cancel" data-item-id="' + item.id + '" type="button">Cancel</button>';
            if (_extUrl) html += '<button class="rq-ws-extlink__delete" data-item-id="' + item.id + '" type="button">Delete</button>';
            html += '</div>';
            html += '</div>';
          }
          // _onBsf === undefined → resolution pending; render nothing (no flash).
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

        // Sub-descriptions area (hidden by default unless has content)
        if (showSubDesc) {
          var hasHandle = !!item.productHandle;
          html += '<div class="rq-ws-subdesc-area' + (hasSubs || hasHandle ? ' rq-ws-subdesc-area--open' : '') + '" data-item-id="' + item.id + '">';
          if (hasSubs) {
            /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — Drag-to-reorder
               sub-text lines. The grip only renders when there are 2+ lines
               to shuffle. `_usedSubIdx` stops indexOf() binding two identical
               lines (two blank ones, say) to the same array slot — that index
               is what the edit and reorder handlers splice on, so a collision
               edits or moves the wrong line. */
            var _usedSubIdx = {};
            var _canDragSubs = visibleSubs.length > 1;
            visibleSubs.forEach(function(sd) {
              // Find the actual index in item.subDescriptions for data binding
              var si = item.subDescriptions.indexOf(sd);
              while (si !== -1 && _usedSubIdx[si]) si = item.subDescriptions.indexOf(sd, si + 1);
              if (si !== -1) _usedSubIdx[si] = true;
              html += '<div class="rq-ws-subdesc-row' + (_canDragSubs ? ' rq-ws-subdesc-row--draggable' : '') + '" data-item-id="' + item.id + '" data-sub-idx="' + si + '">';
              if (_canDragSubs) html += subdescDragHtml(item.id);
              /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
              html += '<button class="rq-ws-subdesc-addmore" data-item-id="' + item.id + '" type="button">&ctdot;</button>';
              /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — 'unknown' rides the SAME amber as
                 TBD. A row reading "Kickstand for Upright Storage: Maybe" is worse than a TBD, not
                 better: TBD announces itself as unfinished, while "Maybe" looks decided and prints
                 that way on the PO. Same treatment, and the Options pill stays live to repair it. */
              var subStatus = subDescStatus(sd, _optMeta);
              var subTitle = subStatus === 'unknown' ? ' title="Not one of this product&#39;s choices — use the Options button to set it"' : '';
              html += '<input class="' + (subStatus ? 'rq-ws-subdesc--tbd' : '') + '"' + subTitle + ' value="' + escAttr(sd) + '" data-item-id="' + item.id + '" data-sub-idx="' + si + '" spellcheck="false">';
              html += '<button class="rq-ws-subdesc-remove" data-item-id="' + item.id + '" data-sub-idx="' + si + '" type="button">&times;</button>';
              html += '</div>';
            });
          }
          html += '</div>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Unit-toggle pill (per-piece ↔ per-sq-ft).
           Renders only for products in UNIT_TOGGLE_PRODUCTS (Heritage Motivate
           today). Click handler in workstation-table.js. */
        var _utCfg = getUnitToggleConfig && getUnitToggleConfig(item);
        if (_utCfg) {
          var _isSqft = item.unitMode === 'sqft';
          var _btnLabel = _isSqft
            ? 'Switch to ' + _utCfg.pieceLabelPlural
            : 'Switch to sq ft';
          var _curLabel = _isSqft ? 'sq ft' : _utCfg.pieceLabelPlural;
          html += '<div class="rq-ws-unit-toggle" data-item-id="' + item.id + '">';
          html += '<span class="rq-ws-unit-toggle__current">Quoted in <strong>' + escHtml(_curLabel) + '</strong></span>';
          html += '<button class="rq-ws-unit-toggle__btn" data-item-id="' + item.id + '" type="button">' + escHtml(_btnLabel) + '</button>';
          html += '</div>';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        // Inline config picker placeholder (always render if item has productHandle or is unconfigured, or if showSubDesc + hasHandle so Configure button works)
        if (item.productHandle || !item.configured) {
          html += '<div class="rq-ws-inline-picker-slot" data-item-id="' + item.id + '"></div>';
        }
        if (isSeriesLast) {
          html += '<button class="rq-ws-series-add-weight" data-series-id="' + escAttr(item.seriesId) + '" type="button"><svg viewBox="0 0 24 16" width="14" height="10"><rect x="1" y="3" width="4" height="10" rx="1" fill="currentColor"/><rect x="19" y="3" width="4" height="10" rx="1" fill="currentColor"/><rect x="4" y="5" width="3" height="6" rx="0.5" fill="currentColor" opacity="0.6"/><rect x="17" y="5" width="3" height="6" rx="0.5" fill="currentColor" opacity="0.6"/><rect x="7" y="7" width="10" height="2" rx="1" fill="currentColor" opacity="0.4"/></svg> add weight</button>';
        }
        html += '</td>';

        // SKU
        html += '<td>';
        if (item.seriesId) {
          html += '<input class="rq-ws-table__input" value="' + escAttr(item.sku || '') + '" data-item-id="' + item.id + '" data-field="seriesSku" spellcheck="false" style="text-align:center;">';
        } else {
          html += '<input class="rq-ws-table__input" value="' + escAttr(item.sku || '') + '" data-item-id="' + item.id + '" data-field="sku" spellcheck="false" style="text-align:center;">';
        }
        /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — the Options pill.

           Opens the inline config picker on a line that ALREADY EXISTS, which is the thing the
           workstation could not do: Q.buildInlineConfigPicker has been fully built since the
           variant-picker work but nothing in the theme ever emitted a launcher for it, so a rep who
           picked the wrong ECORE colour deleted the line and re-added it, and a Legend line's
           "Frame Color: TBD" could only be resolved by typing.

           Covers both product shapes with one control, because the picker already does:
             ECORE — colour IS the variant, so applying moves SKU / cost / MAP / MSRP with it.
             Legend — colour is sub-text against one SKU, so applying rewrites the build spec and
                      re-pins the variant without touching price... except the options that DO carry
                      price (the 3103-L's Kickstand, +$59), which reprice at the rep's own margin.

           Shown only where the sub-text it edits is shown — on a weight series that is the last
           row, same rule as the product-link pill above. */
        if ((!item.seriesId || isSeriesLast) && _optMeta) {
          html += '<button class="rq-ws-options-pill" data-item-id="' + item.id + '" type="button" title="Set this product&#39;s colors and options">&#127912; Options</button>';
        }
        html += '</td>';

        // Qty
        html += '<td><input class="rq-ws-table__input rq-ws-table__input--qty" type="number" min="1" value="' + (item.seriesId ? (item.quantity || '') : (item.quantity || 1)) + '" data-item-id="' + item.id + '" data-field="quantity"></td>';

        // MSRP — `-1` means rep typed "-" to mark the item as "N/A" (no MSRP).
        html += '<td>';
        html += '<input class="rq-ws-table__input" value="' + (item.msrp === -1 ? '-' : (item.msrp ? fmt(item.msrp) : '')) + '" placeholder="$0.00" data-item-id="' + item.id + '" data-field="msrp">';
        html += '</td>';

        // MAP (read-only — current selling price on website)
        /* ⛔ `type === 'custom'` WAS THE WRONG TEST (Ryan 2026-09-01: *"there's no MAP or MSRP on my
           last test"*). It dated from when a custom row had no catalogue behind it and therefore no
           selling price to show. A Weight Designer row IS custom — it carries no variantId until the
           back-fill promotes one — and it DOES have a real MAP, resolved from the product's own
           price. Blanking it hid a figure that was sitting right there on the item, on every row of
           every house-brand quote. Ask whether there IS a price, which is the actual question. */
        html += '<td class="rq-ws-table__derived" style="font-size:12px;color:var(--rq-text-muted);">' + (item.mapPrice ? fmt(item.mapPrice) : '—') + '</td>';

        // Direct (read-only, derived) — shows FREE when item is free, red outline when over MAP
        var directOverMap = !item.isFree && item.directPrice > 0 && item.mapPrice > 0 && item.directPrice > item.mapPrice;
        var directContent = item.isFree ? '<span style="color:var(--rq-success);">FREE</span>' : (item.directPrice ? fmt(item.directPrice) : '–');
        if (directOverMap && !item.isFree && item.directPrice) directContent = '<span style="outline:1px solid var(--rq-error);outline-offset:2px;border-radius:3px;padding:1px 3px;">' + directContent + '</span>';
        html += '<td class="rq-ws-table__derived" data-item-id="' + item.id + '" data-field="directDisplay">' + directContent + '</td>';

        // Total (read-only, derived)
        html += '<td class="rq-ws-table__derived" data-item-id="' + item.id + '" data-field="totalDisplay">' + (item.isFree ? '<span style="color:var(--rq-success);">FREE</span>' : (lineTotal ? fmt(lineTotal) : '–')) + '</td>';

        // Divider
        html += '<td class="rq-ws-table__divider-td"></td>';

        // Cost
        html += '<td><input class="rq-ws-table__input" value="' + (item.cost ? fmt(item.cost) : '') + '" placeholder="$0.00" data-item-id="' + item.id + '" data-field="cost"></td>';

        // Points + Remove
        html += '<td class="rq-ws-table__pts-cell"><input class="rq-ws-table__input rq-ws-table__input--pts" value="' + (item.points ? item.points.toFixed(1) : '') + '" placeholder="0.0" data-item-id="' + item.id + '" data-field="points"><button class="rq-ws-table__remove-btn" data-item-id="' + item.id + '" type="button" title="Remove"><svg viewBox="0 0 20 20" width="100%" height="100%"><line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/><line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg></button></td>';

        html += '</tr>';
      });

      html += '</tbody></table></div></div>';
    });

    // Remove old groups and insert new
    var oldGroups = dom.itemsContainer.querySelectorAll('.rq-ws-vendor-group');
    for (var i = 0; i < oldGroups.length; i++) oldGroups[i].remove();
    dom.itemsEmpty.insertAdjacentHTML('beforebegin', html);

    Q.recalcTotals();
    /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — resolve the product links for any SKU on this
       quote we have not asked about yet. See syncProductLinks: this is the single hook that keeps
       the link pills in step with a changed colour or a newly added item. */
    if (Q.syncProductLinks) Q.syncProductLinks();
  }

  /* ── Duplicate Product Dialog ── */
  function showDuplicateDialog(existing, newQty, itemData) {
    // Remove any existing dialog
    var old = wrap.querySelector('.rq-ws-dup-dialog');
    if (old) old.remove();

    var overlay = document.createElement('div');
    overlay.className = 'rq-ws-dup-dialog';
    overlay.innerHTML =
      '<div class="rq-ws-dup-dialog__card">' +
        '<div class="rq-ws-dup-dialog__title">Product already on quote</div>' +
        '<div class="rq-ws-dup-dialog__product">' + escHtml(existing.title) + '</div>' +
        '<div class="rq-ws-dup-dialog__buttons">' +
          '<button class="rq-ws-dup-dialog__btn rq-ws-dup-dialog__btn--primary" data-action="increase">Increase qty from ' + existing.quantity + ' to ' + newQty + '</button>' +
          '<button class="rq-ws-dup-dialog__btn rq-ws-dup-dialog__btn--secondary" data-action="duplicate">Add as separate line (same price)</button>' +
          '<button class="rq-ws-dup-dialog__btn rq-ws-dup-dialog__btn--cancel" data-action="cancel">Cancel</button>' +
        '</div>' +
      '</div>';

    overlay.addEventListener('click', function(e) {
      var action = e.target.getAttribute('data-action');
      if (!action && e.target === overlay) action = 'cancel';
      if (!action) return;
      overlay.remove();
      if (action === 'increase') {
        existing.quantity = newQty;
        state.dirty = true;
        renderItems();
      } else if (action === 'duplicate') {
        /* CUSTOM_ECORE2027_BY_CLAUDE (Ryan 2026-09-29) — a product ALREADY ON THE QUOTE is locked at
           the quote's price. Ecore honours pre-10/1 pricing for ~30 days on quotes already issued, so
           "more of the same" must not silently arrive at the new catalog price on a separate line
           (it is the rep's call to confirm the old price with Ecore, not the tool's to re-price).
           A product that was DELETED and re-added, or a brand-new quote, goes through the normal
           path and takes today's price. */
        addItem(Object.assign({}, itemData, { _skipDupCheck: true, _inheritPricingFrom: existing }));
      }
    });

    wrap.appendChild(overlay);
  }

  /* ── Add Item ── */
  function addItem(itemData) {
    // Check for duplicate — warn but allow (skip for unconfigured items with null variantId)
    if (!itemData._skipDupCheck && (itemData.variantId || itemData.sku)) {
      for (var di = 0; di < state.items.length; di++) {
        var existing = state.items[di];
        if ((itemData.variantId && existing.variantId === itemData.variantId) ||
            (itemData.sku && itemData.sku === existing.sku && existing.type !== 'custom')) {
          var newQty = (existing.quantity || 1) + 1;
          showDuplicateDialog(existing, newQty, itemData);
          return null;
        }
      }
    }

    var item = {
      id: genId(),
      type: itemData.type || 'product',
      variantId: itemData.variantId || null,
      vendor: normalizeVendor(itemData.vendor || ''),
      title: itemData.title || '',
      sku: itemData.sku || '',
      /* ⚠ `!= null`, NOT `||`. The 1 is a default for an ABSENT quantity, and `||` could not tell
         that from a deliberate 0 — so a Weight Designer row handed over at zero silently arrived as
         one, which is a count nobody chose sitting on a customer's quote. Undefined and null still
         get 1, so every existing caller is unaffected. */
      quantity: itemData.quantity != null ? itemData.quantity : 1,
      msrp: itemData.msrp || 0,
      cost: itemData.cost || 0,
      points: itemData.points || 0,
      directPrice: itemData.directPrice || 0,
      mapPrice: itemData.mapPrice || 0,
      isFree: false,
      subDescriptions: itemData.subDescriptions || [],
      internalNote: '',
      seriesId: itemData.seriesId || null,
      seriesWeight: itemData.seriesWeight != null ? itemData.seriesWeight : null,
      seriesBaseTitle: itemData.seriesBaseTitle || null,
      seriesBaseSku: itemData.seriesBaseSku || null,
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ THE FLAG THAT EXEMPTS PLATES FROM
         QUANTITY PROPAGATION, which this list dropped. `workstation-table.js` propagates a typed
         quantity across a whole series unless `seriesIsPlates` is set; `wdRowsFor` sets it, and
         everything that reached an item through here arrived without it — so `!item.seriesIsPlates`
         was true and PLATES got the copying they were explicitly meant to be exempt from. Invisible
         until now because the native "+ Weight Series" modal pushes straight into `state.items` and
         never passes through this function. */
      seriesIsPlates: !!itemData.seriesIsPlates,
      configured: itemData.configured !== false,
      productHandle: itemData.productHandle || null,
      pendingOptions: itemData.pendingOptions || null,
      customOptions: itemData.customOptions || null,
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Unit-toggle (per-piece ↔ per-sq-ft).
         Only applies to products in UNIT_TOGGLE_PRODUCTS (e.g. Heritage
         Motivate). Default 'piece' = how the row arrives. Toggling scales
         qty/msrp/cost and rewrites the "Quoted as" sub-text. */
      unitMode: itemData.unitMode || 'piece'
    };

    // Auto-calculate if we have cost + mapPrice
    var _lock = itemData._inheritPricingFrom;   // CUSTOM_ECORE2027_BY_CLAUDE — see showDuplicateDialog
    if (_lock) {
      item.cost = parseFloat(_lock.cost) || 0;
      item.points = parseFloat(_lock.points) || 0;
      item.directPrice = parseFloat(_lock.directPrice) || 0;
      item.mapPrice = parseFloat(_lock.mapPrice) || 0;
      item.msrp = parseFloat(_lock.msrp) || 0;
      item.isFree = !!_lock.isFree;
    } else if (item.cost > 0 && item.mapPrice > 0) {
      item.points = autoPoints(item.cost, item.mapPrice);
      item.directPrice = item.mapPrice; // Start at MAP
    } else if (item.cost > 0 && item.points > 0) {
      item.directPrice = calculateDirect(item.cost, item.points);
    }

    // MSRP stays blank if no compareAtPrice — MAP column shows the selling price separately

    state.items.push(item);

    // Note: vendor color sub relocation removed — render logic now handles
    // shared vs item-specific display without needing subs pre-copied between items.

    if (!itemData._skipRender) {
      dom.searchInput.value = '';
      Q.closeSearch();
      renderItems();
    }
    return item;
  }

  /* ── Vendor Option Preferences ── */
  // Remembers option selections per vendor+optionName+values combo
  // Key: "vendor|prefGroup|val1,val2,val3" → chosen value
  // Option names within a group share preferences (e.g. Frame Color = Pull-Up Bar Color)
  var vendorPrefs = {};

  // Legend Fitness option name groups — options in the same group share color preferences
  var PREF_GROUPS = {
    // Group A: Main frame / powder coat
    'Frame Color': 'frame',
    'Frame Color (vertical uprights)': 'frame',
    'Frames (vertical uprights)': 'frame',
    'Frames Color (vertical uprights)': 'frame',
    'Shroud & Frame Color': 'frame',
    'Nameplate Color': 'frame',
    'Ladder Sides Color': 'frame',
    'Powder Coat Color': 'frame',
    // Group B: Upholstery
    'Pad Color': 'pad',
    'Upholstery Color': 'pad',
    // Group C: Secondary frame (crossmembers, accessories)
    'Frame Color (crossmembers & attachments)': 'frame2',
    'Frames (crossmembers & attachments)': 'frame2',
    'Frame Color (crossmembers & arms)': 'frame2',
    'Frame Color (crossmembers, attachments & shrouds)': 'frame2',
    /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — the secondary powder-coat surfaces that are
       LINE-ITEM PROPERTIES rather than variant options (theme template blocks; see
       src/lib/legend-template-options.js in rfq-worker). Grouped with the frame because that is
       exactly what the storefront does — assets/custom-legend-color-mirror.js mirrors Frame Color
       into these four when the customer leaves them unset, and legend-build-spec.js prints the same
       default on the PO. Listing them here gets the picker three things for free: pref auto-fill
       from the frame colour, propagation across the vendor group, and the shared-colour collapse on
       the quote PDF that Frame Color already gets.

       ⛔ READ-ONLY MEMBERS OF THE GROUP. Nothing may savePref() from these labels — a rep building
       a two-tone machine (black frame, red shroud) would otherwise write "red" into the shared
       `frame` slot and the NEXT Legend line would come out red. The picker reads the group pref for
       custom options and never writes it; keep it that way. */
    'Metal Shroud Color': 'frame',
    'Front Plate Color': 'frame',
    'Rear Plate Color': 'frame',
    'Optional Arm Color': 'frame',
    'Pull-Up Bar Color': 'frame2',
    'Shelf Color': 'frame2',
    'Ladder Rungs Color': 'frame2',
  };

  function getPrefGroup(optionName) {
    return PREF_GROUPS[optionName] || optionName;
  }

  function getPrefKey(vendor, optionName, values) {
    var group = getPrefGroup(optionName);
    // Grouped options use group-only key (share across different value sets)
    // Ungrouped options include sorted values for exact matching
    if (group !== optionName) {
      return vendor + '|' + group;
    }
    var sorted = values.slice().sort();
    return vendor + '|' + group + '|' + sorted.join(',');
  }

  // Extract prefix from a sub-description (part before ":")
  function subDescPrefix(sd) { var c = sd.indexOf(':'); return c > -1 ? sd.substring(0, c).trim() : sd; }

  /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — what choices does this line's product actually
     offer, from BOTH carriers?

     `o` is the variant options (ECORE's Color, Legend's Frame/Pad/Kickstand). `co` is everything
     that is NOT a variant option: `custom-opt:` tags plus, since 2026-08-24, the theme template
     blocks the worker now folds in (Metal Shroud Color, Storage Pegs Per Side, and on a collapsed
     "-nocolor" product, Frame Color and Pad Color themselves). Read together they are the whole
     answer to "is this sub-text row a real choice or a rep's free note?".

     ⚠️ Keyed lowercased. A rep types the label as often as the picker writes it, and "frame color"
     must not read as a different option from "Frame Color". */
  /* CUSTOM_QWOPTIONLABEL_BY_CLAUDE (Ryan 2026-10-10) — a colour line names its option.
     A floor imported from the Gym Layout Designer carries its colour as a bare sub-text line ("Steel Appeal -
     ES15A"). The Options picker only recognises "Name: value" lines, so changing the colour ADDED "Color: Basic
     Black - ES00" under the old line instead of replacing it. Before every render: a line that is exactly one of
     the product's option values becomes "Color: <value>" in the same place; and where a quote already carries
     both (the old bare line and a newer labelled one), the newer value takes the OLD line's position and the
     duplicate goes, so the lines keep their order. Lines that are not an option value (rep notes) are never
     touched. Returns true when it changed something. */
  function labelOptionSubs(item) {
    if (!item || !Array.isArray(item.subDescriptions) || !item.subDescriptions.length) return false;
    var meta = optionMetaFor(item);
    if (!meta) return false;
    var subs = item.subDescriptions.slice();
    var changed = false;
    /* A value two options share ("Black" as Frame AND Pad colour) is not guessed into either. */
    function ownerCount(val) {
      var v = String(val).trim().toLowerCase(), n = 0;
      Object.keys(meta.byName).forEach(function(k) {
        if ((meta.byName[k].values || []).some(function(x) { return String(x).trim().toLowerCase() === v; })) n++;
      });
      return n;
    }
    Object.keys(meta.byName).forEach(function(key) {
      var opt = meta.byName[key];
      var values = (opt.values || []).map(function(v) { return String(v).trim().toLowerCase(); });
      var bareIdx = -1, labelIdx = -1;
      subs.forEach(function(sd, i) {
        var s = String(sd || '').trim();
        var colon = s.indexOf(':');
        if (colon !== -1 && s.substring(0, colon).trim().toLowerCase() === key) { if (labelIdx === -1) labelIdx = i; return; }
        if (bareIdx === -1 && values.indexOf(s.toLowerCase()) !== -1 && ownerCount(s) === 1) bareIdx = i;
      });
      if (bareIdx === -1) return;
      if (labelIdx === -1) {
        subs[bareIdx] = opt.name + ': ' + String(subs[bareIdx]).trim();
      } else if (bareIdx < labelIdx) {
        subs[bareIdx] = subs[labelIdx];
        subs.splice(labelIdx, 1);
      } else {
        subs.splice(bareIdx, 1);
      }
      changed = true;
    });
    if (changed) item.subDescriptions = subs;
    return changed;
  }
  Q.labelOptionSubs = labelOptionSubs;

  function optionMetaFor(item) {
    if (!item || !item.productHandle) return null;
    var cp = (Q.catalogByHandle || {})[item.productHandle];
    if (!cp) return null;
    var byName = {}, choices = 0;
    (cp.o || []).forEach(function(o) {
      if (!o || !o.n || !o.v || o.v.length < 2) return;   // a 1-value option is not a choice
      byName[String(o.n).trim().toLowerCase()] = { name: o.n, values: o.v, custom: false };
      choices++;
    });
    (cp.co || []).forEach(function(co) {
      if (!co || !co.l || !co.v || !co.v.length) return;
      var k = String(co.l).trim().toLowerCase();
      if (byName[k]) return;                               // a variant option of the same name wins
      byName[k] = { name: co.l, values: co.v, custom: true };
      choices++;
    });
    return choices ? { byName: byName, count: choices } : null;
  }

  /* How a sub-text row should read to the rep:
       'tbd'     — placeholder the picker seeded, nothing chosen yet
       'unknown' — the LABEL is a real option but the VALUE is not one of its choices. This is the
                   "Kickstand for Upright Storage: Maybe" case: it looks settled, prints verbatim on
                   the PO, and means nothing to the factory.
       ''        — a real choice, OR a row whose label matches no option at all, which is a
                   legitimate rep note ("Deliver dock-high only") and must never be flagged.
     Ryan 2026-08-24: flag it, never block on it — the picker is the repair tool. */
  function subDescStatus(sd, meta) {
    if (/\bTBD\b/i.test(sd)) return 'tbd';
    if (!meta) return '';
    var colon = sd.indexOf(':');
    if (colon === -1) return '';
    var opt = meta.byName[sd.substring(0, colon).trim().toLowerCase()];
    if (!opt) return '';
    var val = sd.substring(colon + 1).trim();
    if (!val) return '';
    for (var i = 0; i < opt.values.length; i++) {
      if (String(opt.values[i]).trim() === val) return '';
    }
    return 'unknown';
  }


  // Detect sub-descriptions that are vendor-wide color preferences (should relocate to last item)
  function isVendorColorSubDesc(sd) {
    var colon = sd.indexOf(':');
    if (colon === -1) return false;
    var prefix = sd.substring(0, colon).trim();
    if (PREF_GROUPS.hasOwnProperty(prefix)) return true;
    if (/^(Frames?|Pads?)\b/i.test(prefix)) return true;
    return false;
  }

  function savePref(vendor, optionName, values, chosenValue) {
    vendorPrefs[getPrefKey(vendor, optionName, values)] = chosenValue;
  }

  function getPref(vendor, optionName, values) {
    var pref = vendorPrefs[getPrefKey(vendor, optionName, values)];
    // Verify the preferred value exists in this option's values
    if (pref && values.indexOf(pref) === -1) return null;
    return pref || null;
  }

  // Build a display title with only non-preference options in the suffix
  function buildItemTitle(product, options, selectedOpts, prefResolved) {
    var manualParts = [];
    for (var oi = 0; oi < options.length; oi++) {
      if (!prefResolved[oi] && selectedOpts[oi]) {
        manualParts.push(selectedOpts[oi]);
      }
    }
    var suffix = manualParts.length > 0 ? ' - ' + manualParts.join(' / ') : '';
    // Q.quoteTitle applies the per-handle quote-name override, then stripVendor.
    // Falls back to the local stripVendor if search.js hasn't registered yet.
    var base = Q.quoteTitle ? Q.quoteTitle(product) : stripVendor(product.title, product.vendor);
    return base + suffix;
  }

  // Build sub-descriptions from preference-resolved options
  function buildPrefSubDescs(options, prefResolved, selectedOpts) {
    var descs = [];
    for (var oi = 0; oi < options.length; oi++) {
      if (prefResolved[oi]) {
        descs.push(options[oi].name + ': ' + selectedOpts[oi]);
      }
    }
    return descs;
  }

  // Find first non-null compareAtPrice: check inline variants, then product-level catalog field
  function findCompareAtPrice(variants, product) {
    for (var i = 0; i < variants.length; i++) {
      var cap = parseFloat(variants[i].compareAtPrice);
      if (cap > 0) return cap;
    }
    if (product && product.firstCompareAtPrice) return parseFloat(product.firstCompareAtPrice) || 0;
    return 0;
  }

  // Check if a product's variants have different SKUs (e.g., flooring colors, Airex colors)
  // When true, the variant picker must be shown — auto-applying prefs would select the wrong SKU
  function variantsHaveUniqueSKUs(variants) {
    if (!variants || variants.length < 2) return false;
    var firstSku = null;
    for (var i = 0; i < variants.length; i++) {
      var sku = (variants[i].sku || '').trim();
      if (!sku) continue;
      if (firstSku === null) { firstSku = sku; continue; }
      if (sku !== firstSku) return true;
    }
    return false;
  }

  // stripVendor is needed by buildItemTitle — use Q.stripVendor from search module,
  // but since items.js loads before search.js, provide a local fallback
  function stripVendor(title, vendor) {
    if (Q.stripVendor) return Q.stripVendor(title, vendor);
    if (!vendor || !title) return title;
    if (title.toLowerCase().indexOf(vendor.toLowerCase()) === 0) {
      var stripped = title.substring(vendor.length).replace(/^[\s\-–—:]+/, '').trim();
      return stripped || title;
    }
    return title;
  }

  // Register on namespace for cross-module access
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — Sub-text drag grip.
     The same six-dot handle the item rows carry, sized down for the 12px
     sub-text row. The markup lives here so the full render above and the
     DOM surgery in workstation-table.js (which inserts and removes rows
     WITHOUT a re-render) can't drift apart. Drag logic: workstation-reorder.js. */
  var SUBDESC_DRAG_SVG = '<svg viewBox="0 0 12 16" width="8" height="12" fill="currentColor" aria-hidden="true"><circle cx="3" cy="3" r="1.5"/><circle cx="3" cy="8" r="1.5"/><circle cx="3" cy="13" r="1.5"/><circle cx="9" cy="3" r="1.5"/><circle cx="9" cy="8" r="1.5"/><circle cx="9" cy="13" r="1.5"/></svg>';

  function subdescDragHtml(itemId) {
    return '<button class="rq-ws-subdesc-drag" data-item-id="' + escAttr(itemId) +
      '" type="button" aria-label="Drag to reorder" title="Drag to reorder">' + SUBDESC_DRAG_SVG + '</button>';
  }

  /* Reconcile the grips in one sub-text area after a row was added or removed
     in place. A lone line has nothing to reorder against, so its grip goes away.

     It also re-stamps data-sub-idx from row position — but ONLY when the rows
     account for the whole array. The add/remove handlers splice positionally,
     so after they run every row below the change point carries a stale index;
     renumbering repairs that. When a shared vendor colour is collapsed onto
     another item the array is longer than the rows, position is NOT the index,
     and re-stamping would corrupt bindings the full render got right — so in
     that case leave the indices exactly as rendered. */
  function refreshSubdescHandles(area) {
    if (!area) return;
    var itemId = area.getAttribute('data-item-id') || '';
    var rows = area.querySelectorAll('.rq-ws-subdesc-row');
    var want = rows.length > 1;
    var item = Q.findItem ? Q.findItem(itemId) : null;
    var subs = (item && item.subDescriptions) || [];
    var canRenumber = (subs.length === rows.length);
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var handle = row.querySelector('.rq-ws-subdesc-drag');
      if (want && !handle) row.insertAdjacentHTML('afterbegin', subdescDragHtml(itemId));
      else if (!want && handle) handle.remove();
      row.classList.toggle('rq-ws-subdesc-row--draggable', want);
      row.setAttribute('data-item-id', itemId);
      var idx = i;
      if (!canRenumber) {
        var inp0 = row.querySelector('input');
        var raw = inp0 ? inp0.getAttribute('data-sub-idx') : null;
        if (raw === null) continue;
        idx = parseInt(raw, 10);
        row.setAttribute('data-sub-idx', String(idx));
        continue;
      }
      row.setAttribute('data-sub-idx', String(idx));
      row.querySelectorAll('[data-sub-idx]').forEach(function(el) {
        el.setAttribute('data-sub-idx', String(idx));
      });
    }
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  Q.renderItems = renderItems;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-16) — renderItems() without losing the rep's place.

     renderItems() removes every vendor group and re-inserts the lot, which destroys whatever the
     rep had focused; the browser then hands focus to the next focusable element and scroll-snaps
     to it, usually the top of the page. Four callers already guard against that by hand (+5 and
     remove-X in workstation-table.js, both indent paths in workstation-reorder.js) with
     blur → snapshot scrollY → restore.

     This is that pattern with one addition, for callers that fire ASYNCHRONOUSLY — the
     custom-catalog SKU autofill lands from a fetch, by which time the rep has tabbed on to the
     next cell. Blurring alone would leave them typing into nothing, so the focused cell is found
     again by item + field after the rebuild and its caret put back. Scroll is restored LAST so it
     is the final word (the refocus runs preventScroll for the same reason). */
  function renderItemsInPlace() {
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    var active = document.activeElement;
    var focusRef = null;
    if (active && dom.itemsContainer && dom.itemsContainer.contains(active) && active.getAttribute) {
      var fId = active.getAttribute('data-item-id');
      var fField = active.getAttribute('data-field');
      if (fId && fField) {
        focusRef = { id: fId, field: fField, start: null, end: null };
        try { focusRef.start = active.selectionStart; focusRef.end = active.selectionEnd; } catch (_) {}
      }
    }
    if (active && typeof active.blur === 'function') active.blur();

    renderItems();

    if (focusRef && dom.itemsContainer) {
      var again = dom.itemsContainer.querySelector(
        'input[data-item-id="' + focusRef.id + '"][data-field="' + focusRef.field + '"]');
      if (again && typeof again.focus === 'function') {
        try { again.focus({ preventScroll: true }); } catch (_) { again.focus(); }
        if (focusRef.start !== null) {
          try { again.setSelectionRange(focusRef.start, focusRef.end); } catch (_) {}
        }
      }
    }
    window.scrollTo(0, y);
  }
  Q.renderItemsInPlace = renderItemsInPlace;
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE — Load saved per-SKU external product links for
     the current quote's items (PDF product-links feature), then re-render so
     rows with a saved link show the chip instead of "+ Add product link". */
  /* CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — WHICH SKUs HAVE WE ACTUALLY ASKED ABOUT?

     `state.onBsf` / `bsfUrls` / `externalLinks` are keyed by **SKU**, and a SKU is not fixed for the
     life of a line: adding an item introduces one, and changing an ECORE colour REPLACES one
     (24 colours, 24 SKUs). This map is what lets a render tell "we asked and the answer was no"
     apart from "we have never asked", which the two render branches need and could not previously
     get from the data. Keys are marked resolved when the answer comes back — including SKUs the
     response had nothing to say about, so one unanswerable SKU cannot make every render re-ask. */
  var linkResolvedSkus = {};
  var linkFetchInFlight = false;
  var linkRetryAfter = 0;
  Q.linkResolvedSkus = linkResolvedSkus;

  Q.fetchExternalLinks = function() {
    if (!state.linkProductsOnPdf) return;
    if (linkFetchInFlight) return;
    if (linkRetryAfter && Date.now() < linkRetryAfter) return;
    // Send each item's SKU + productHandle + title so the worker can resolve
    // "is it on our site?" robustly (handle -> SKU -> precise title match) —
    // the SAME resolver the PDF uses, so the button-gate and the PDF agree.
    /* Only SKUs we have not already resolved. On a fresh quote that is all of them, so load
       behaviour is unchanged; afterwards it is just the one SKU a colour change introduced. */
    var items = [];
    var seen = {};
    (state.items || []).forEach(function(it) {
      if (!it || !it.sku) return;
      if (seen[it.sku] || linkResolvedSkus[it.sku]) return;
      seen[it.sku] = 1;
      items.push({ sku: it.sku, handle: it.productHandle || '', title: it.title || '' });
    });
    if (!items.length) return;
    var api = Q.API || (window.RepPortal && window.RepPortal.API);
    if (!api || !api.post) return;
    linkFetchInFlight = true;
    api.post('/product-external-link', { items: items }).then(function(resp) {
      linkFetchInFlight = false;
      if (!resp) return;
      /* ⛔ MERGE, NEVER REPLACE. These used to be wholesale assignments, which was correct only
         while the request always carried every SKU in the quote. Now that it carries just the
         unresolved ones, replacing would wipe the answers for every other line on the quote. */
      if (resp.map) { state.externalLinks = state.externalLinks || {}; for (var a in resp.map) state.externalLinks[a] = resp.map[a]; }
      if (resp.onBsf) { state.onBsf = state.onBsf || {}; for (var b in resp.onBsf) state.onBsf[b] = resp.onBsf[b]; }
      if (resp.bsfUrls) { state.bsfUrls = state.bsfUrls || {}; for (var c in resp.bsfUrls) state.bsfUrls[c] = resp.bsfUrls[c]; }
      /* ⛔ CUSTOM_QWOPTIONS_BY_CLAUDE (Ryan 2026-08-24) — MARK RESOLVED ONLY WHAT WAS ANSWERED.

         This used to mark every SKU in the request, which turns a partial answer into a permanent
         wrong one. The worker builds `onBsf` inside a try/catch and returns `{}` on any Shopify
         failure — a token refresh, a throttle, a timeout — and `{}` is truthy, so the merge above
         runs over zero keys and every SKU in the batch got flagged "asked and answered" carrying no
         answer at all. The render then reads `undefined`, sees the SKU marked resolved, and offers
         "Add link" on a product that is on our site. It never retries, because as far as this map
         is concerned the question was settled.

         That is RW-1283-A: TRD-000093-A is ACTIVE and published, and the worker's own query returns
         it for all four SKUs on that quote — checked directly against Shopify. Nothing was wrong
         with the product or the resolver; the client recorded a non-answer as an answer.

         Now an unanswered SKU stays unresolved and the next render asks again, behind the same 30s
         backoff that guards a hard failure. */
      var answered = resp.onBsf || {};
      var anyAnswered = false;
      items.forEach(function(it) {
        if (Object.prototype.hasOwnProperty.call(answered, it.sku)) { linkResolvedSkus[it.sku] = 1; anyAnswered = true; }
      });
      /* Leave a record. This failure is silent on both ends — the worker swallows the Shopify error
         and returns {}, and the UI just draws the wrong button — so the one console line is the
         only way anyone finds out which SKUs went unanswered. */
      if (!anyAnswered) {
        linkRetryAfter = Date.now() + 30000;
        if (window.console && console.warn) {
          console.warn('[QW] product-link resolve returned no answer for:',
            items.map(function(it) { return it.sku; }).join(', '), '— retrying in 30s. Response:', resp);
        }
      }
      renderItems();
    }).catch(function() {
      /* Not marked resolved — a network blip should not permanently strip a real Blue Sky link off
         the quote. Backed off instead, because renderItems() drives this and a bare retry would
         fire a request per keystroke. */
      linkFetchInFlight = false;
      linkRetryAfter = Date.now() + 30000;
    });
  };

  /* Called at the end of every render. Every path that can introduce a new SKU — adding an item,
     applying a colour in the Options picker, typing a SKU to swap variants, a preference resolving
     a TBD line — ends in a render, so asking here covers all of them without each one having to
     remember to. Cheap: it no-ops unless a SKU on the quote has never been asked about. */
  function syncProductLinks() {
    if (!state.linkProductsOnPdf) return;
    var pending = false;
    (state.items || []).forEach(function(it) {
      if (it && it.sku && !linkResolvedSkus[it.sku]) pending = true;
    });
    if (pending) Q.fetchExternalLinks();
  }
  Q.syncProductLinks = syncProductLinks;
  Q.addItem = addItem;
  Q.showDuplicateDialog = showDuplicateDialog;
  Q.PREF_GROUPS = PREF_GROUPS;
  Q.vendorPrefs = vendorPrefs;
  Q.getPrefGroup = getPrefGroup;
  Q.getPrefKey = getPrefKey;
  Q.subDescPrefix = subDescPrefix;
  Q.optionMetaFor = optionMetaFor;
  Q.subDescStatus = subDescStatus;
  Q.subdescDragHtml = subdescDragHtml;
  Q.refreshSubdescHandles = refreshSubdescHandles;
  Q.isVendorColorSubDesc = isVendorColorSubDesc;
  Q.savePref = savePref;
  Q.getPref = getPref;
  Q.buildItemTitle = buildItemTitle;
  Q.buildPrefSubDescs = buildPrefSubDescs;
  Q.findCompareAtPrice = findCompareAtPrice;
  Q.variantsHaveUniqueSKUs = variantsHaveUniqueSKUs;
})();
